import { ref, type Ref } from 'vue';
import Accounting from 'api/accounting';
import Companies from 'api/companies';
import Payments from 'api/payments';
import Stock from 'api/stock';
import Ocr from 'api/ocr';
import Feature from 'Services/Feature';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { PaymentType } from 'types/entities/payment/paymentType.type';
import type { Warehouse } from 'types/entities/stock/warehouse.type';
import type { RawTag } from 'types/entities/smartTags/tag.type';
import type { PresetSupplier } from 'types/entities/company/third.type';

interface UseOcrDictionariesReturn {
    accountingCodes: Ref<AccountingCode[]>;
    taxRates: Ref<TaxRate[]>;
    paymentTypes: Ref<PaymentType[]>;
    warehouses: Ref<Warehouse[]>;
    defaultAccountingCode: Ref<number | null>;
    presetSupplier: Ref<PresetSupplier[]>;
    defaultTags: Ref<RawTag[]>;
    loadDictionaries: () => Promise<void>;
}

export const useOcrDictionaries = (): UseOcrDictionariesReturn => {
    const accountingCodes = ref<AccountingCode[]>([]);
    const taxRates = ref<TaxRate[]>([]);
    const paymentTypes = ref<PaymentType[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const defaultAccountingCode = ref<number | null>(null);
    const presetSupplier = ref<PresetSupplier[]>([]);
    const defaultTags = ref<RawTag[]>([]);

    const loadDictionaries = async (): Promise<void> => {
        const [
            accountingCodesResponse,
            taxesResponse,
            defaultAccountingCodeResponse,
            companiesResponse,
            paymentTypesResponse,
            tagsResponse,
            warehousesResponse,
        ] = await Promise.all([
            Accounting.getAllAccountingCodes(
                { limit: 100, order: 'code' },
                { filters: { views: ['purchase'] } },
            ),
            Accounting.getTaxes({ limit: 100 }, { filters: { is_active: true } }),
            Accounting.getDefaultAccountingPurchaseCode(),
            Companies.getCompanies(
                { limit: 10, order: 'created_at', direction: 'desc', field: ['id', 'name'] },
                { type: 'supplier' },
            ),
            Payments.getAllPayments(),
            Ocr.getTagsPurInvoice(),
            Feature.isEnabled('ocr-stock')
                ? Stock.getWarehouses().catch(() => [])
                : Promise.resolve([]),
        ]);

        accountingCodes.value = accountingCodesResponse as AccountingCode[];
        taxRates.value = (taxesResponse.data.data as TaxRate[]).map((tax) => ({
            ...tax,
            rate: parseFloat(tax.rate).toFixed(2),
        }));
        defaultAccountingCode.value = +defaultAccountingCodeResponse;
        presetSupplier.value = companiesResponse.data.data as PresetSupplier[];
        paymentTypes.value = paymentTypesResponse as PaymentType[];
        defaultTags.value = tagsResponse as RawTag[];
        warehouses.value = (warehousesResponse ?? []) as Warehouse[];
    };

    return {
        accountingCodes,
        taxRates,
        paymentTypes,
        warehouses,
        defaultAccountingCode,
        presetSupplier,
        defaultTags,
        loadDictionaries,
    };
};
